import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import SEO from '../components/SEO';
import '../styles/products.css';
import { PRODUCT_DATA, SECTIONS } from '../data/productsData';

const CustomSelect = memo(({ value, onChange, options, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="custom-select" ref={selectRef}>
      <button 
        className={`custom-select__trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={label}
      >
        <span>{selectedOption?.label}</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M4 6l4 4 4-4z"/>
        </svg>
      </button>
      {isOpen && (
        <div className="custom-select__dropdown">
          {options.map(option => (
            <button
              key={option.value}
              className={`custom-select__option ${value === option.value ? 'selected' : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
              {value === option.value && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M13 4L6 11 3 8"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

const ProductGrid = memo(({ products, prices = {} }) => {
  const [flippedIds, setFlippedIds] = useState({});

  if (products.length === 0) {
    return <div className="products__empty">검색 결과가 없습니다</div>;
  }

  return (
    <div className="products__grid">
      {products.map((product) => {
        const isFlipped = flippedIds[product.id];
        const price = prices[product.id];
        return (
          <div key={product.id} className="products__item">
            <div className="products__image-container">
              <img
                src={isFlipped && product.backImage ? product.backImage : product.image}
                alt={product.name}
                className="products__image"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/images/placeholder.webp';
                }}
              />
              {product.backImage && (
                <button 
                  className="products__flip-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFlippedIds(prev => ({ ...prev, [product.id]: !prev[product.id] }));
                  }}
                  aria-label={isFlipped ? '앞면 보기' : '뒷면 보기'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12h18M3 12l4-4m-4 4l4 4M21 12l-4-4m4 4l-4 4"/>
                  </svg>
                </button>
              )}
            </div>
            <div className="products__content">
              <h3 className="products__name">{product.name}</h3>
              {product.weight && <p className="products__weight">{product.weight}</p>}
              <p className="products__price">{price ? `${price.toLocaleString()}원` : '가격 문의'}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
});

const Products = () => {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const mainRef = useRef(null);

  const scrollToMain = useCallback(() => {
    if (mainRef.current) {
      const headerHeight = window.innerWidth <= 768 ? 60 : 80;
      const toolbarHeight = document.querySelector('.products__toolbar')?.offsetHeight || 0;
      const offset = headerHeight + toolbarHeight;
      const elementPosition = mainRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get('category');
    if (categoryParam && SECTIONS.some(s => s.id === categoryParam)) {
      setCategory(categoryParam);
      setTimeout(scrollToMain, 100);
    }
  }, [scrollToMain]);

  useEffect(() => {
    const handleParamsChange = () => {
      const params = new URLSearchParams(window.location.search);
      const categoryParam = params.get('category');
      if (categoryParam && SECTIONS.some(s => s.id === categoryParam)) {
        setCategory(categoryParam);
        setTimeout(scrollToMain, 100);
      } else if (!categoryParam) {
        setCategory('all');
      }
      setSearchTerm('');
      setSortBy('default');
    };
    window.addEventListener('popstate', handleParamsChange);
    return () => window.removeEventListener('popstate', handleParamsChange);
  }, [scrollToMain]);

  useEffect(() => {
    const handleScroll = () => {
      const toolbar = document.querySelector('.products__toolbar');
      if (toolbar) {
        toolbar.classList.toggle('scrolled', window.scrollY > 300);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch('https://koreakookmingold.com/data/prices.json')
      .then(res => res.json())
      .then(data => setPrices(data.prices || {}))
      .catch(err => {
        console.error('Failed to fetch prices:', err);
        setPrices({});
      })
      .finally(() => setLoading(false));
  }, []);

  const categoryOptions = [
    { value: 'all', label: '전체 상품' },
    { value: 'goldBars', label: '골드바' },
    { value: 'silverBars', label: '실버바' },
    { value: 'jewelry', label: '골드 주얼리' },
    { value: 'luckyKeys', label: '행운의 열쇠' },
    { value: 'animals', label: '동물' }
  ];

  const sortOptions = [
    { value: 'default', label: '기본 정렬' },
    { value: 'price-asc', label: '가격 낮은순' },
    { value: 'price-desc', label: '가격 높은순' }
  ];

  const handleCategoryChange = useCallback((newCategory) => {
    setCategory(newCategory);
    setSearchTerm('');
    setSortBy('default');
    window.history.replaceState(null, '', newCategory === 'all' ? '/products' : `/products?category=${newCategory}`);
    setTimeout(scrollToMain, 50);
  }, [scrollToMain]);

  const filteredProductsBySection = useMemo(() => {
    const sectionsToShow = category === 'all' ? SECTIONS : SECTIONS.filter(s => s.id === category);
    const term = searchTerm.toLowerCase().trim();
    
    return sectionsToShow.reduce((result, section) => {
      let products = PRODUCT_DATA[section.products].map(p => ({ ...p, price: prices[p.id] || 0 }));

      if (term) {
        products = products.filter(p => 
          p.name.toLowerCase().includes(term) || p.weight?.toLowerCase().includes(term)
        );
      }

      if (sortBy !== 'default') {
        products.sort((a, b) => 
          sortBy === 'price-asc' ? (a.price || 0) - (b.price || 0) : (b.price || 0) - (a.price || 0)
        );
      }

      result[section.id] = products;
      return result;
    }, {});
  }, [prices, searchTerm, category, sortBy]);

  return (
    <>
      <SEO 
        title="순금제품 - 한국국민금거래소"
        description="한국국민금거래소의 순금 제품을 만나보세요. 금괴, 행운의 열쇠, 주얼리, 은괴 등 다양한 귀금속 제품 안내"
        keywords="순금제품, 금괴, 금반지, 금목걸이, 금팔찌, 행운의열쇠, 은괴, 귀금속, 한국국민금거래소"
        url="https://koreakookmingold.com/products"
      />

      <section className="products__hero">
        <div className="products__hero-content">
          <h1 className="products__hero-title">순금제품</h1>
          <p className="products__hero-subtitle">
            한국국민금거래소와 함께<br />
            최고의 순금제품을 만나보세요
          </p>
        </div>
      </section>

      <div className="products__toolbar">
        <div className="products__toolbar-container">
          <div className="products__search">
            <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input 
              type="text" 
              placeholder="상품명 또는 무게로 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="상품 검색"
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => setSearchTerm('')} aria-label="검색어 지우기">✕</button>
            )}
          </div>
          <div className="products__filters">
            <CustomSelect 
              value={category}
              onChange={handleCategoryChange}
              options={categoryOptions}
              label="카테고리 필터"
            />
            <CustomSelect 
              value={sortBy}
              onChange={setSortBy}
              options={sortOptions}
              label="정렬 방식"
            />
          </div>
        </div>
      </div>

      <main className="products__main" ref={mainRef}>
        <div className="products__container">
          <div className="products__section-header">
            <h2 className="products__section-title">한국국민금거래소의 제품</h2>
            <div className="products__section-divider"></div>
          </div>

          {loading ? (
            <div className="products__loading">
              <div className="loading-spinner"></div>
              <p>상품 정보를 불러오는 중입니다</p>
            </div>
          ) : (
            SECTIONS.map((section, index, array) => {
              const sectionProducts = filteredProductsBySection[section.id];
              if (!sectionProducts?.length) return null;
              return (
                <div key={section.id} className="products__category">
                  <h3 className="products__category-title">
                    {section.title}
                    <span className="category-count">{sectionProducts.length}개</span>
                  </h3>
                  <ProductGrid 
                    products={sectionProducts} 
                    prices={prices}
                  />
                  {index < array.length - 1 && <hr className="products__separator" />}
                </div>
              );
            })
          )}
        </div>
      </main>
    </>
  );
};

export default Products;